/**
 * Units (the invaders on the map) are entities, not cells: a 16 × 16 box that queries the grid.
 * They walk pixel by pixel with a maximum step height, fall, swim, and are stamped into the grid during
 * step() so that sand piles on them.
 */
import { Physique, STATE_LIQUID, wakeRect, cosDeg, sinDeg, TRIG_ONE, type World } from '@invaders/sim';
import { FP, FP_SHIFT, type GameState, type Unit } from './state';
import { INVADERS, LIZARDMAN, scaledDamage, type DamageKind } from './invaders';

const EMPTY_ID = Physique.EMPTY;
const ENTITY_ID = Physique.ENTITY;
const LIQUID = STATE_LIQUID;

/** GDD section 7: the sprite is 16 × 16 and its feet touch the ground. */
export const UNIT_W = 16;
export const UNIT_H = 16;
export const UNIT_HP = 100;
/** Highest step a unit climbs while walking. */
export const MAX_STEP = 5;
/** Cells per tick, fixed-point: about 22 cells per second. */
export const WALK_SPEED = 96;
export const GRAVITY = 16;
export const MAX_FALL_SPEED = 6 * FP;
/** About 18 cells high: a bit more than a body. */
export const JUMP_VY = -384;
export const JUMP_VX = 128;
/** Falls up to this many cells are harmless; each cell beyond costs 1 HP. Not in the GDD: tuning value. */
export const SAFE_FALL = 48;
/** The fall damage is this percent of 1 HP per cell beyond SAFE_FALL (author's choice: 60 % less). */
export const FALL_DAMAGE_PERCENT = 40;
/** Top speed when sinking through a liquid (a liquid also breaks a fall: no fall damage). */
export const MAX_SINK_SPEED = FP >> 1;
export const SWIM_SPEED = 64;
/** A stroke upward when jumping in a liquid. */
export const SWIM_STROKE_VY = -160;
/** Gliders (Zorg, Cloudman) fall at most this fast, and take no fall damage. */
export const GLIDE_FALL_SPEED = FP;
/** Heavy units (Robotman) sink like a stone. */
export const HEAVY_SINK_SPEED = 2 * FP;
/** GDD: ice is slippery. A unit that stops walking on ice slides on for this many ticks. */
export const ICE_SLIDE_TICKS = 30;
/**
 * Jetpack (author's addition): 6 s of thrust (3, then doubled). Thrust up and sideways in cells per tick² (fixed-point FP; the gravity
 * is 16), and the speeds it reaches. Flying is not falling: the fall damage counts from where the thrust stops.
 */
export const JETPACK_FUEL = 6 * 60;
export const JETPACK_THRUST = 28;
export const JETPACK_SIDE = 10;
export const JETPACK_MAX_UP = FP;
export const JETPACK_MAX_SIDE = FP + (FP >> 2);
/** Lizardman leaves a drop of acid behind every this many cells walked. */
export const ACID_TRAIL_STEP = 4;
/**
 * Lava is thick (author's choice): units sink and swim in it this many times slower than in water. They no longer
 * float on it (the author's rule of before): they sink, and drown in it as in water (`noDrown` of the invaders).
 */
export const LAVA_SLOWDOWN = 2;
/**
 * Jump pads (author's addition, the level editor): the speed up they give, fixed-point FP per tick. 3.5 cells per tick:
 * about 98 cells high under the normal gravity (an ordinary jump: 18), three times higher on the Moon. Chosen value.
 */
export const PAD_VY = 896;

/** True if the unit whose top row is `cy` stands on a jump pad (its feet right above the pad's row). */
export function onPad(state: GameState, unit: Unit, cy: number): boolean {
  const pads = state.map.pads;
  if (pads.length === 0) return false;
  const feet = cy + UNIT_H;
  const ux = unit.x >> FP_SHIFT;
  for (const p of pads) if (feet === p.y && ux + UNIT_W > p.x0 && ux <= p.x1) return true;
  return false;
}

/** Walking into another unit pushes it a little (author's addition): one cell for every PUSH_EVERY cells walked. */
export const PUSH_EVERY = 3;
/**
 * The drill as a way to move (author's change; the GDD digs a tunnel of 64 cells at once): DRILL_TICKS at most, at
 * DRILL_SPEED (cells per tick, fixed-point FP: about the walk, some 68 cells in all), its bit DRILL_REACH cells ahead of
 * the body's centre digging a disc of DRILL_RADIUS (a tunnel 20 across, as the GDD's).
 */
export const DRILL_TICKS = 3 * 60;
export const DRILL_SPEED = 96;
export const DRILL_RADIUS = 10;
export const DRILL_REACH = 8;
/** A 'drill' event (debris, sound) every this many ticks of boring. */
const DRILL_EVENT_EVERY = 6;

/**
 * True if the body box at cell (cx, cy) overlaps nothing solid: units move through gases and liquids.
 * Outside the map is free: a unit pushed out of it is lost (see updateUnit).
 */
export function boxFree(world: World, cx: number, cy: number): boolean {
  const w = world.physique.w;
  const h = world.physique.h;
  const data = world.physique.data;
  const state = world.rules.materials.state;
  const x0 = cx < 0 ? 0 : cx;
  const x1 = cx + UNIT_W > w ? w : cx + UNIT_W;
  const y0 = cy < 0 ? 0 : cy;
  const y1 = cy + UNIT_H > h ? h : cy + UNIT_H;
  for (let y = y0; y < y1; y++) {
    const row = y * w;
    for (let x = x0; x < x1; x++) {
      if (state[data[row + x]] < LIQUID) return false;
    }
  }
  return true;
}

function isLiquidAt(world: World, x: number, y: number): boolean {
  const { w, h, data } = world.physique;
  return x >= 0 && x < w && y >= 0 && y < h && world.rules.materials.state[data[y * w + x]] === LIQUID;
}

/**
 * Liquid level in column x, from the feet row up to the surface: solids at the bottom (a slope under
 * the water) are skipped. 0 if the column is dry at its bottom, -1 if it is solid over the whole body
 * height (a wall: it says nothing about the water level).
 */
function sideLevel(world: World, x: number, top: number, feet: number): number {
  const { w, data } = world.physique;
  if (x < 0 || x >= w) return -1;
  const state = world.rules.materials.state;
  let surface = -1;
  for (let y = feet; y >= 0; y--) {
    const s = y < world.physique.h ? state[data[y * w + x]] : LIQUID + 1;
    if (s === LIQUID) { surface = y; continue; }
    if (surface >= 0) break;
    if (s > LIQUID) return 0;
    if (y <= top) return -1;
  }
  return surface < 0 ? 0 : feet + 1 - surface;
}

/**
 * Depth of liquid above the unit's feet: the level beside its body (the lower side, walls ignored), so
 * drops sitting on its head don't count. In a shaft walled on both sides, the column through its body is used.
 */
export function liquidDepth(world: World, unit: Unit): number {
  const cx = unit.x >> FP_SHIFT;
  const top = unit.y >> FP_SHIFT;
  const feet = top + UNIT_H - 1;
  const left = sideLevel(world, cx - 1, top, feet);
  const right = sideLevel(world, cx + UNIT_W, top, feet);
  if (left >= 0 && right >= 0) return left < right ? left : right;
  if (left >= 0) return left;
  if (right >= 0) return right;
  const mid = cx + (UNIT_W >> 1);
  let surface = feet + 1;
  for (let y = feet; y >= 0 && (y >= top || isLiquidAt(world, mid, y)); y--) {
    if (isLiquidAt(world, mid, y)) surface = y;
    else if (surface <= feet) break;
  }
  return feet + 1 - surface;
}

/**
 * The head is under a liquid the unit drowns in: the whole body in a level of liquid (rain drops or a geyser's spray do
 * not count), with water, lava, oil or acid at head height that is not in its `noDrown` (author's rule).
 */
export function isHeadUnder(world: World, unit: Unit): boolean {
  if (!unit.alive || liquidDepth(world, unit) < UNIT_H) return false;
  const { w, h, data } = world.physique;
  const matState = world.rules.materials.state;
  const safe = INVADERS[unit.kind].noDrown;
  const cx = unit.x >> FP_SHIFT;
  const y = (unit.y >> FP_SHIFT) + 1;
  if (y < 0 || y >= h) return false;
  for (let x = Math.max(0, cx - 1); x <= Math.min(w - 1, cx + UNIT_W); x++) {
    const m = data[y * w + x];
    if (matState[m] === LIQUID && !safe.includes(m)) return true;
  }
  return false;
}

/** True if any cell of the body, or around it, is `mat` (contact damage, GDD section 3). */
export function touches(world: World, unit: Unit, mat: number): boolean {
  const { w, h, data } = world.physique;
  const cx = unit.x >> FP_SHIFT;
  const cy = unit.y >> FP_SHIFT;
  for (let y = Math.max(0, cy - 1); y <= Math.min(h - 1, cy + UNIT_H); y++) {
    for (let x = Math.max(0, cx - 1); x <= Math.min(w - 1, cx + UNIT_W); x++) if (data[y * w + x] === mat) return true;
  }
  return false;
}

/** True if lava is in the body or right under its feet: the unit walks or swims in it, not only beside it. */
export function onLava(world: World, unit: Unit): boolean {
  const { w, h, data } = world.physique;
  const cx = unit.x >> FP_SHIFT;
  const cy = unit.y >> FP_SHIFT;
  for (let y = Math.max(0, cy); y <= Math.min(h - 1, cy + UNIT_H); y++) {
    for (let x = Math.max(0, cx); x < Math.min(w, cx + UNIT_W); x++) if (data[y * w + x] === Physique.LAVA) return true;
  }
  return false;
}

/** True if the middle of the body is in a liquid. */
export function isSwimming(world: World, unit: Unit): boolean {
  return isLiquidAt(world, (unit.x >> FP_SHIFT) + (UNIT_W >> 1), (unit.y >> FP_SHIFT) + (UNIT_H >> 1));
}

/** How many times slower the unit sinks and swims where it is: LAVA_SLOWDOWN in lava (the middle of its body), else 1. */
function thickness(world: World, unit: Unit): number {
  const { w, h, data } = world.physique;
  const x = (unit.x >> FP_SHIFT) + (UNIT_W >> 1);
  const y = (unit.y >> FP_SHIFT) + (UNIT_H >> 1);
  return x >= 0 && x < w && y >= 0 && y < h && data[y * w + x] === Physique.LAVA ? LAVA_SLOWDOWN : 1;
}

export function createUnit(id: number, player: number, kind: number, cellX: number, cellY: number): Unit {
  return {
    id, player, kind,
    x: cellX << FP_SHIFT, y: cellY << FP_SHIFT,
    vx: 0, vy: 0,
    hp: UNIT_HP, alive: true,
    facing: player % 2 === 0 ? 1 : -1, walkDir: 0, walkAcc: 0,
    fallStartY: cellY,
    burning: false, fuse: 0, wet: false, frozen: 0, slide: 0, puppet: false, jet: 0, thrust: false, airJump: false,
    drill: 0, drillAngle: 0,
  };
}

/** Centre of the body, in cells. */
export function unitCenter(unit: Unit): [number, number] {
  return [(unit.x >> FP_SHIFT) + (UNIT_W >> 1), (unit.y >> FP_SHIFT) + (UNIT_H >> 1)];
}

/** Drops the unit straight down from its current row onto the first support. */
export function placeOnGround(world: World, unit: Unit): void {
  const cx = unit.x >> FP_SHIFT;
  let cy = unit.y >> FP_SHIFT;
  while (cy < world.physique.h && boxFree(world, cx, cy + 1)) cy++;
  unit.y = cy << FP_SHIFT;
  unit.fallStartY = cy;
}

/** Applies `amount` of `kind` damage after the unit's sensitivity (GDD section 6). */
/**
 * Friendly fire off (author's option, MatchConfig.friendlyFire): the unit belongs to the team whose weapon is acting
 * (state.source), so it takes neither damage nor cold from it. Fire, lava, acid, water and falls still hurt.
 */
export function spared(state: GameState, unit: Unit): boolean {
  return state.source >= 0 && unit.player === state.source && state.match?.config.friendlyFire === false;
}

/** The player of the unit `id` (the owner of a projectile, a mine, a wave), -1 if none. */
export function ownerPlayer(state: GameState, id: number): number {
  return id >= 0 && id < state.units.length ? state.units[id].player : -1;
}

export function damageUnit(state: GameState, unit: Unit, amount: number, kind: DamageKind = 'direct'): void {
  if (!unit.alive || spared(state, unit)) return;
  amount = scaledDamage(unit.kind, kind, amount);
  if (amount <= 0) return;
  unit.hp -= amount;
  state.events.push({ type: 'damage', unit: unit.id, amount });
  if (unit.hp <= 0) killUnit(state, unit);
}

export function healUnit(unit: Unit, amount: number): void {
  if (unit.alive) unit.hp = Math.min(UNIT_HP, unit.hp + amount);
}

/** `offMap`: the unit left the map, so it leaves nothing behind (no death burst, no stele). */
export function killUnit(state: GameState, unit: Unit, offMap = false): void {
  if (!unit.alive) return;
  unit.hp = 0;
  unit.alive = false;
  unit.walkDir = 0;
  unit.puppet = false;
  state.events.push({ type: 'death', unit: unit.id, offMap });
}

/** One cell sideways, as a walker: up a step if needed, then down a slope; false if a wall stops it. */
function stepAside(world: World, unit: Unit, dir: number): boolean {
  const cx = unit.x >> FP_SHIFT;
  const cy = unit.y >> FP_SHIFT;
  const nx = cx + dir;
  let climbed = -1;
  for (let up = 0; up <= MAX_STEP; up++) {
    if (boxFree(world, nx, cy - up)) { climbed = up; break; }
  }
  if (climbed < 0) return false;
  let ny = cy - climbed;
  // Follow downward slopes instead of hopping off every step.
  let down = 0;
  while (down < MAX_STEP && boxFree(world, nx, ny + down + 1)) down++;
  if (!boxFree(world, nx, ny + down + 1)) ny += down;
  unit.x = nx << FP_SHIFT;
  unit.y = ny << FP_SHIFT;
  return true;
}

/**
 * The units the walker runs into are pushed one cell along (author's addition): those standing ahead of it whose
 * box overlaps its own. They step like walkers (up a step, down a slope), never through a wall; a unit in the air,
 * tied to a voodoo doll or frozen is not pushed.
 */
function pushAhead(state: GameState, unit: Unit): void {
  const dir = unit.walkDir;
  const x = unit.x >> FP_SHIFT;
  const y = unit.y >> FP_SHIFT;
  for (const other of state.units) {
    if (other === unit || !other.alive || other.puppet || other.frozen !== 0) continue;
    const ox = other.x >> FP_SHIFT;
    const oy = other.y >> FP_SHIFT;
    if ((ox - x) * dir < 0 || Math.abs(ox - x) >= UNIT_W || Math.abs(oy - y) >= UNIT_H) continue;
    if (boxFree(state.world, ox, oy + 1)) continue;
    stepAside(state.world, other, dir);
  }
}

/** Walking at the unit's speed; every PUSH_EVERY cells, it pushes the units it runs into. */
function walk(state: GameState, unit: Unit): void {
  unit.walkAcc += Math.trunc((WALK_SPEED * INVADERS[unit.kind].speed) / 100);
  while (unit.walkAcc >= FP) {
    unit.walkAcc -= FP;
    if (!stepAside(state.world, unit, unit.walkDir)) { unit.walkAcc = 0; return; }
    if ((unit.x >> FP_SHIFT) % PUSH_EVERY === 0) pushAhead(state, unit);
  }
}

function land(state: GameState, unit: Unit, cellY: number): void {
  // Under a lower gravity (the Moon), a fall lands as softly as one `gravityDiv` times shorter.
  const div = state.map.gravityDiv;
  const fell = cellY - unit.fallStartY;
  // A jump pad takes the fall (it throws the unit up again at the next tick).
  if (fell > SAFE_FALL * div && !INVADERS[unit.kind].glide && !onPad(state, unit, cellY)) {
    damageUnit(state, unit, Math.trunc(((fell - SAFE_FALL * div) * FALL_DAMAGE_PERCENT) / (100 * div)), 'fall');
  }
  unit.vx = 0;
  unit.vy = 0;
  unit.fallStartY = cellY;
}

function moveVertical(state: GameState, unit: Unit): void {
  const world = state.world;
  const target = unit.y + unit.vy;
  const cx = unit.x >> FP_SHIFT;
  let cy = unit.y >> FP_SHIFT;
  const targetCell = target >> FP_SHIFT;
  const dir = targetCell > cy ? 1 : -1;
  while (cy !== targetCell) {
    if (!boxFree(world, cx, cy + dir)) {
      unit.y = cy << FP_SHIFT;
      if (dir > 0) land(state, unit, cy);
      else unit.vy = 0;
      return;
    }
    cy += dir;
    if (cy < unit.fallStartY) unit.fallStartY = cy;
  }
  unit.y = target;
}

function moveHorizontal(world: World, unit: Unit): void {
  if (unit.vx === 0) return;
  const target = unit.x + unit.vx;
  let cx = unit.x >> FP_SHIFT;
  const cy = unit.y >> FP_SHIFT;
  const targetCell = target >> FP_SHIFT;
  const dir = targetCell > cx ? 1 : -1;
  while (cx !== targetCell) {
    if (!boxFree(world, cx + dir, cy)) {
      unit.x = cx << FP_SHIFT;
      unit.vx = 0;
      return;
    }
    cx += dir;
  }
  unit.x = target;
}

/** True if one of the cells right under the unit's feet is `mat`. */
export function standsOn(world: World, unit: Unit, mat: number): boolean {
  const { w, h, data } = world.physique;
  const cx = unit.x >> FP_SHIFT;
  const y = (unit.y >> FP_SHIFT) + UNIT_H;
  if (y < 0 || y >= h) return false;
  for (let x = Math.max(0, cx); x < Math.min(w, cx + UNIT_W); x++) if (data[y * w + x] === mat) return true;
  return false;
}

/** GDD: Lizardman leaves a trail of acid as he walks (a drop at his heels). */
function acidTrail(world: World, unit: Unit): void {
  const { w, h, data } = world.physique;
  const x = unit.facing > 0 ? (unit.x >> FP_SHIFT) - 1 : (unit.x >> FP_SHIFT) + UNIT_W;
  const y = (unit.y >> FP_SHIFT) + UNIT_H - 1;
  if (x < 0 || x >= w || y < 0 || y >= h || data[y * w + x] !== Physique.EMPTY) return;
  data[y * w + x] = Physique.ACID;
  world.cellState.data[y * w + x] = 0;
  wakeRect(world, x, y, x, y);
}

export function isGrounded(world: World, unit: Unit): boolean {
  return !boxFree(world, unit.x >> FP_SHIFT, (unit.y >> FP_SHIFT) + 1);
}

/**
 * Moving in a jump (author's addition): in the air, the walking keys push the unit toward AIR_SPEED in their
 * direction (they never slow down a faster blow in that direction).
 */
export const AIR_SPEED = 128;
export const AIR_ACCEL = 12;
function airControl(unit: Unit): void {
  if (unit.walkDir > 0 && unit.vx < AIR_SPEED) unit.vx = Math.min(AIR_SPEED, unit.vx + AIR_ACCEL);
  else if (unit.walkDir < 0 && unit.vx > -AIR_SPEED) unit.vx = Math.max(-AIR_SPEED, unit.vx - AIR_ACCEL);
}

/** Jetpack thrust of a tick: up while held, sideways with the walking keys in the air; it burns the fuel. */
function jetThrust(unit: Unit, grounded: boolean): void {
  const up = unit.thrust;
  const side = !grounded && unit.walkDir !== 0;
  if (up) unit.vy = Math.max(-JETPACK_MAX_UP, unit.vy - JETPACK_THRUST);
  if (side) unit.vx = Math.max(-JETPACK_MAX_SIDE, Math.min(JETPACK_MAX_SIDE, unit.vx + unit.walkDir * JETPACK_SIDE));
  if (up || side) {
    unit.jet--;
    unit.fallStartY = unit.y >> FP_SHIFT;
  } else if (grounded && unit.vy >= 0 && unit.jet < JETPACK_FUEL) unit.jet = 0; // landed after flying
  if (unit.jet <= 0) { unit.jet = 0; unit.thrust = false; }
}

/** Starts boring with the drill in direction `angle` (the jetpack comes off, a doll's link breaks). */
export function startDrill(unit: Unit, angle: number): void {
  if (!unit.alive) return;
  unit.drill = DRILL_TICKS;
  unit.drillAngle = angle;
  unit.jet = 0;
  unit.thrust = false;
  unit.puppet = false;
}

/**
 * A tick of drilling. The unit bores its way in the drill's direction while the bit bites into the ground (a solid or
 * a powder just ahead of it): it digs the disc and the body's box (rock and obsidian included: the author's rule; not the
 * liquids) and moves on, without gravity. In the open the bit has nothing to grip: false, the usual physics apply
 * (it falls), the drill still turning. It stops at the edges of the map.
 */
function drillTick(state: GameState, unit: Unit): boolean {
  unit.drill--;
  const world = state.world;
  const { w, h, data } = world.physique;
  const cells = world.cellState.data;
  const matState = world.rules.materials.state;
  const cos = cosDeg(unit.drillAngle);
  const sin = sinDeg(unit.drillAngle);
  const [cx, cy] = unitCenter(unit);
  const bx = cx + Math.trunc((DRILL_REACH * cos) / TRIG_ONE);
  const by = cy - Math.trunc((DRILL_REACH * sin) / TRIG_ONE);
  const r2 = DRILL_RADIUS * DRILL_RADIUS;
  // The bit bites if there is ground just ahead of it: in the half disc in front, a little wider than what it digs.
  const g = DRILL_RADIUS + 2;
  let bites = false;
  for (let y = Math.max(0, by - g); y <= Math.min(h - 1, by + g) && !bites; y++) {
    for (let x = Math.max(0, bx - g); x <= Math.min(w - 1, bx + g); x++) {
      const dx = x - bx;
      const dy = y - by;
      if (dx * dx + dy * dy > g * g || dx * cos - dy * sin < 0) continue;
      const m = data[y * w + x];
      if (matState[m] < LIQUID) { bites = true; break; }
    }
  }
  if (!bites) return false;
  const nx = unit.x + Math.trunc((DRILL_SPEED * cos) / TRIG_ONE);
  const ny = unit.y - Math.trunc((DRILL_SPEED * sin) / TRIG_ONE);
  const ncx = nx >> FP_SHIFT;
  const ncy = ny >> FP_SHIFT;
  if (ncx < 0 || ncy < 0 || ncx + UNIT_W > w || ncy + UNIT_H > h) {
    unit.drill = 0;
    return false;
  }
  const dig = (x: number, y: number) => {
    const i = y * w + x;
    if (matState[data[i]] < LIQUID) { data[i] = Physique.EMPTY; cells[i] = 0; } // obsidian too (author's rule)
  };
  for (let y = Math.max(0, by - DRILL_RADIUS); y <= Math.min(h - 1, by + DRILL_RADIUS); y++) {
    for (let x = Math.max(0, bx - DRILL_RADIUS); x <= Math.min(w - 1, bx + DRILL_RADIUS); x++) {
      if ((x - bx) * (x - bx) + (y - by) * (y - by) <= r2) dig(x, y);
    }
  }
  for (let y = ncy; y < ncy + UNIT_H; y++) for (let x = ncx; x < ncx + UNIT_W; x++) dig(x, y);
  wakeRect(world, Math.min(bx - DRILL_RADIUS, ncx) - 1, Math.min(by - DRILL_RADIUS, ncy) - 1, Math.max(bx + DRILL_RADIUS, ncx + UNIT_W) + 1, Math.max(by + DRILL_RADIUS, ncy + UNIT_H) + 1);
  unit.x = nx;
  unit.y = ny;
  unit.vx = 0;
  unit.vy = 0;
  unit.fallStartY = ncy;
  if (cos !== 0) unit.facing = cos < 0 ? -1 : 1;
  if (unit.drill % DRILL_EVENT_EVERY === 0) state.events.push({ type: 'drill', x0: bx, y0: by, x1: bx, y1: by });
  return true;
}

export function updateUnit(state: GameState, unit: Unit): void {
  const world = state.world;
  // Frozen (author's rule): in its block of ice, it neither walks nor slides, flies nor drills; it still falls.
  if (unit.frozen !== 0) { unit.walkDir = 0; unit.walkAcc = 0; unit.slide = 0; unit.jet = 0; unit.thrust = false; unit.drill = 0; }
  const cx = unit.x >> FP_SHIFT;
  const cy = unit.y >> FP_SHIFT;
  const grounded = !boxFree(world, cx, cy + 1);
  const swimming = isSwimming(world, unit);
  if (unit.jet > 0) jetThrust(unit, grounded);

  if (unit.drill > 0 && drillTick(state, unit)) {
    // Moved by its drill.
  } else if (unit.puppet) {
    // Copying a voodoo doll: its speed is set by the doll each tick; a collision breaks the link.
    const vx = unit.vx;
    const vy = unit.vy;
    moveVertical(state, unit);
    moveHorizontal(world, unit);
    if (unit.vx !== vx || unit.vy !== vy) unit.puppet = false;
  } else if (grounded && unit.vy >= 0) {
    unit.vx = 0;
    unit.vy = 0;
    unit.fallStartY = cy;
    unit.airJump = false;
    const x0 = unit.x >> FP_SHIFT;
    const onIce = standsOn(world, unit, Physique.ICE);
    if (unit.walkDir !== 0) {
      walk(state, unit);
      unit.slide = onIce ? ICE_SLIDE_TICKS : 0;
    } else if (unit.slide > 0) {
      // Sliding on: the speed of a walk, in the direction it faces, until it leaves the ice.
      if (onIce) { unit.walkDir = unit.facing; walk(state, unit); unit.walkDir = 0; unit.slide--; } else unit.slide = 0;
    }
    if (unit.kind === LIZARDMAN && (unit.x >> FP_SHIFT) !== x0 && ((unit.x >> FP_SHIFT) % ACID_TRAIL_STEP) === 0) acidTrail(world, unit);
    // On a jump pad (landed on it, or walked onto it): thrown up, it flies from the next tick.
    const ny = unit.y >> FP_SHIFT; // a step climbed while walking
    if (onPad(state, unit, ny)) {
      unit.vy = -PAD_VY;
      unit.slide = 0;
      state.events.push({ type: 'pad', unit: unit.id, x: (unit.x >> FP_SHIFT) + (UNIT_W >> 1), y: ny + UNIT_H });
    }
  } else {
    const kind = INVADERS[unit.kind];
    unit.vy += Math.trunc(GRAVITY / state.map.gravityDiv);
    const maxFall = kind.glide ? GLIDE_FALL_SPEED : MAX_FALL_SPEED;
    if (unit.vy > maxFall) unit.vy = maxFall;
    if (swimming) {
      // Lava is thick: there it sinks and swims LAVA_SLOWDOWN times slower.
      const slow = thickness(world, unit);
      const sink = Math.trunc((kind.heavy ? HEAVY_SINK_SPEED : MAX_SINK_SPEED) / slow);
      if (unit.vy > sink) unit.vy = sink;
      unit.fallStartY = cy;
      // Swimming: the arrows push the unit along; a knockback slows down in the liquid.
      if (unit.walkDir !== 0) unit.vx = Math.trunc((unit.walkDir * SWIM_SPEED * kind.swim) / (100 * slow));
      else unit.vx = Math.trunc((unit.vx * 7) / 8);
    } else if (unit.walkDir !== 0 && unit.jet === 0) airControl(unit);
    moveVertical(state, unit);
    moveHorizontal(world, unit);
  }

  // Drowning, the author's rule (instead of the GDD's 10 HP per turn): the head under water, lava, oil or acid kills at
  // once, except in the unit's own matter (Fishman water, Lavaman lava, Lizardman acid) and never Cloudman.
  if (unit.alive && isHeadUnder(world, unit)) { killUnit(state, unit); return; }

  // GDD: leaving the map by a side or by the bottom kills everyone. A unit thrown out by the top falls back, on the
  // Moon too (the author's choice: the GDD loses it in space there).
  const [mx] = unitCenter(unit);
  if (mx < 0 || mx >= world.physique.w || unit.y >> FP_SHIFT >= world.physique.h) killUnit(state, unit, true);
}

/** At rest: dead, or standing on something without moving (a unit in a liquid sinks to the bottom). */
export function isSettled(world: World, unit: Unit): boolean {
  if (!unit.alive) return true;
  if (unit.puppet || unit.drill !== 0 || unit.walkDir !== 0 || unit.slide !== 0 || unit.vx !== 0 || unit.vy !== 0) return false;
  return isGrounded(world, unit);
}

/** Puts the jetpack on: a full tank, the thrust released. */
export function putJetpackOn(unit: Unit): void {
  if (!unit.alive || unit.jet > 0) return;
  unit.jet = JETPACK_FUEL;
  unit.thrust = false;
}

/** A jump; in the air, once, a second one as high as the first (double jump, author's addition). */
export function jump(world: World, unit: Unit): void {
  if (unit.frozen !== 0) return;
  if (isSwimming(world, unit)) {
    unit.vy = Math.trunc(SWIM_STROKE_VY / thickness(world, unit)); // a stroke goes half as far in thick lava
    return;
  }
  if (unit.jet > 0 || unit.puppet) return; // with the jetpack on, the jump key is its thrust
  if (!isGrounded(world, unit)) {
    if (unit.airJump) return;
    unit.airJump = true;
  }
  // Height grows with the square of the take-off speed: twice as high = ×√2 (ratio 181/128).
  unit.vy = INVADERS[unit.kind].jump >= 200 ? Math.trunc((JUMP_VY * 181) / 128) : JUMP_VY;
  unit.vx = JUMP_VX * unit.facing;
}

/** Stamps (or clears) living units into the grid so the sand simulation treats them as solid. */
export function stampUnits(world: World, units: readonly Unit[], stamp: boolean): void {
  const w = world.physique.w;
  const h = world.physique.h;
  const data = world.physique.data;
  const from = stamp ? EMPTY_ID : ENTITY_ID;
  const to = stamp ? ENTITY_ID : EMPTY_ID;
  for (const unit of units) {
    if (!unit.alive) continue;
    const cx = unit.x >> FP_SHIFT;
    const cy = unit.y >> FP_SHIFT;
    for (let y = cy < 0 ? 0 : cy; y < cy + UNIT_H && y < h; y++) {
      for (let x = cx < 0 ? 0 : cx; x < cx + UNIT_W && x < w; x++) {
        const idx = y * w + x;
        if (data[idx] === from) data[idx] = to;
      }
    }
    // Powders around a unit must react when it arrives or leaves.
    wakeRect(world, cx, cy, cx + UNIT_W - 1, cy + UNIT_H - 1);
  }
}
